import { useRef } from 'react';

import { useIntl } from 'react-intl';

import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { syncBlockMessages } from '@atlaskit/editor-common/messages';
import { SYNCED_BLOCKS_DOCUMENTATION_URL } from '@atlaskit/editor-common/sync-block';
import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { cardPlugin, type CardPlugin } from '@atlaskit/editor-plugin-card';
import { extensionPlugin, type ExtensionPlugin } from '@atlaskit/editor-plugin-extension';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { gridPlugin, type GridPlugin } from '@atlaskit/editor-plugin-grid';
import { type HyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import { listPlugin, type ListPlugin } from '@atlaskit/editor-plugin-list';
import { type MaxContentSizePlugin } from '@atlaskit/editor-plugin-max-content-size';
import { mentionsPlugin, type MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import { type PlaceholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { saveOnEnterPlugin, type SaveOnEnterPlugin } from '@atlaskit/editor-plugin-save-on-enter';
import { textColorPlugin, type TextColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { type TypeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type LinkPickerUiOptions } from '@atlaskit/link-picker';
import { fg } from '@atlaskit/platform-feature-flags';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import { arrowKeyPlugin, useArrowkeyPlugin, type UserMessage } from './arrow-key-plugin';
import {
	connectorSourceInputRulePlugin,
	type ConnectorSourceEntry,
} from './connector-source-input-rule';
import messages from './messages';
import { pasteTextColorPlugin } from './paste-text-color-plugin';

/**
 * The maximum number of characters that can be entered in the chat input.
 */
export const MAX_CHARACTERS_INPUT = 256000;

type EditorPresetProps = {
	onSave: (view: EditorView) => void;
	productKey: string;
	siteId: string;
	arrowKeyPluginConfig: {
		userMessages: UserMessage[];
	};
	placeholderPrompts?: string[];
	placeholderText?: string;
	/** Optional override for Link Picker options */
	linkPickerOptions?: LinkPickerUiOptions;
	/**
	 * Typically used when the container is small (e.g., chromeless editor with small width).
	 */
	useShortMessages?: boolean;
	connectorSources?: ConnectorSourceEntry[];
};

export const useEditorPreset = ({
	onSave,
	arrowKeyPluginConfig,
	productKey,
	siteId,
	placeholderPrompts,
	placeholderText,
	linkPickerOptions = {},
	useShortMessages = false,
	connectorSources = [],
}: EditorPresetProps): {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preset: any;
	/**
	 * This type needs to be defined explicitly otherwise we get deep import errors.
	 * This is a non-exhaustive list of plugins that are used in our preset - there are more default plugins but we want to avoid adding these to package.json if they are not used.
	 */
	editorApi?: PublicPluginAPI<
		[
			CardPlugin,
			SaveOnEnterPlugin,
			MaxContentSizePlugin,
			MentionsPlugin,
			GridPlugin,
			QuickInsertPlugin,
			ListPlugin,
			TypeAheadPlugin,
			HyperlinkPlugin,
			FocusPlugin,
			PlaceholderPlugin,
			ExtensionPlugin,
			TextColorPlugin,
		]
	>;
} => {
	const { formatMessage } = useIntl();
	const linkPicker = useLinkPickerEditorProps(linkPickerOptions, {
		cloudId: siteId,
		product: productKey,
	});

	const { handleArrowDown, handleArrowUp, handleInputChange } = useArrowkeyPlugin({
		userMessages: arrowKeyPluginConfig.userMessages,
	});

	const knownSourcesRef = useRef<ConnectorSourceEntry[]>(connectorSources);
	knownSourcesRef.current = connectorSources;

	const { preset, editorApi } = usePreset(() => {
		const isUpdatedPlaceholderEnabled: () => boolean = () =>
			FeatureGates.getExperimentValue('rovo-chat-input-placeholder-updated', 'isEnabled', false);

		const placeholderConfig =
			placeholderPrompts && fg('aifc_create_enabled')
				? { placeholderPrompts }
				: placeholderText
					? { placeholder: placeholderText }
					: {
							placeholder: formatMessage(
								useShortMessages
									? messages.placeholderShort
									: isUpdatedPlaceholderEnabled()
										? messages.placeholderUpdated
										: messages.placeholder,
							),
						};

		return createDefaultPreset({
			placeholder: placeholderConfig,
			paste: {
				pasteWarningOptions: fg('platform_synced_block_unsupported_products')
					? {
							cannotPasteSyncedBlock: {
								title: syncBlockMessages.cannotPasteSyncedBlockTitle,
								description: syncBlockMessages.cannotPasteSyncedBlockUnsupportedDescription,
								urlHref: SYNCED_BLOCKS_DOCUMENTATION_URL,
								urlText: syncBlockMessages.cannotPasteSyncedBlockAction,
							},
						}
					: undefined,
			},
			hyperlinkOptions: {
				linkPicker,
				lpLinkPicker: true,
			},
			blockType: {
				allowBlockType: {
					exclude: ['heading'],
				},
				lastNodeMustBeParagraph: false,
			},
			toolbar: {
				enableNewToolbarExperience: false,
			},
		})
			.add([saveOnEnterPlugin, onSave])
			.add(mentionsPlugin)
			.add(gridPlugin)
			.add([
				cardPlugin,
				{
					allowBlockCards: false,
					allowEmbeds: false,
					linkPicker,
					lpLinkPicker: true,
					provider: Promise.resolve(editorCardProvider),
				},
			])
			.add(listPlugin)
			.add(extensionPlugin)
			.maybeAdd(
				[connectorSourceInputRulePlugin, { knownSourcesRef }],
				FeatureGates.getExperimentValue(
					'rovo_chat_3p_app_discovery_m1_experiment',
					'isEnabled',
					false,
					{
						fireExperimentExposure: false,
					},
				),
			)
			.add(textColorPlugin)
			.add(pasteTextColorPlugin)
			.add([
				arrowKeyPlugin,
				{
					onArrowDown: handleArrowDown,
					onArrowUp: handleArrowUp,
					onInputChange: handleInputChange,
				},
			]);
	}, []);

	return {
		preset,
		editorApi,
	};
};
